import './contact-us-section.sass';
import { Container, Row, Col, Badge, Button } from 'react-bootstrap';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import Axios from 'axios';
import { Trans } from 'react-i18next';
import { useTranslation } from 'react-i18next';
import { FaUser } from 'react-icons/fa';
import { MdEmail, MdMessage } from 'react-icons/md';
import useRecaptcha from '../recaptcha';
import ReCAPTCHA from 'react-google-recaptcha';
import { ToastContainer, toast } from 'react-toastify';
import { FaFeatherPointed } from 'react-icons/fa6';

import 'react-toastify/dist/ReactToastify.css';

interface FormValues {
  name: string;
  email: string;
  subject: string;
  message: string;
  reCaptchaToken: string;
}

function ContactUs() {
  return (
    <Container fluid className="contact-container">
      <Row className="justify-content-center">
        <Col className="form-col">
          <Forms />
          <ToastContainer position="bottom-right" />
        </Col>
      </Row>
    </Container>
  );
}

function Forms() {
  const { recaptchaRef, handleRecaptcha } = useRecaptcha();
  const { t, i18n } = useTranslation();
  const captchaKey = '6LfSMQUkAAAAAHikNnjkI2WAMi9S-GfcU23cZ3LD';

  const initialValues: FormValues = {
    name: '',
    email: '',
    subject: '',
    message: '',
    reCaptchaToken: '',
  };

  const NAME_MIN = 3;
  const NAME_MAX = 50;
  const SUBJECT_MIN = 3;
  const SUBJECT_MAX = 30;
  const MESSAGE_MIN = 10;
  const MESSAGE_MAX = 500;

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required(t('name_required'))
      .min(NAME_MIN, t('name_minlength'))
      .max(NAME_MAX, t('name_maxlength'))
      .matches(
        /^[A-Za-zÀ-ÖØ-öø-ÿ\u00C0-\u024F\u1E00-\u1EFF\s'’-]+$/,
        t('regex_name')
      ),

    email: Yup.string().email(t('invalid_email')).required(t('email_required')),
    subject: Yup.string()
      .required(t('subject_required'))
      .min(SUBJECT_MIN, t('subject_minlength'))
      .max(SUBJECT_MAX, t('subject_maxlength')),

    message: Yup.string()
      .required(t('message_required'))
      .min(MESSAGE_MIN, t('message_minlength'))
      .max(MESSAGE_MAX, t('message_maxlength')),

    reCaptchaToken: Yup.string().required(t('captcha_required')),
  });

  const handleSubmit = async (
    values: FormValues,
    { resetForm }: { resetForm: () => void }
  ) => {
    // Get the current language selected
    const lang = i18n.language.split('-')[0];

    // Show toast message during submission
    const toastId = toast.info(t('submitting'), {
      pauseOnHover: false,
      autoClose: 3000,
      isLoading: true,
    });

    try {
      const formData = { ...values, lang };
      // Make API request
      const response = await Axios.post(
        '/api/communications-service/public/contact-us',

        formData,
        {
          timeout: 5000,
        }
      );

      if (response.status === 200) {
        toast.update(toastId, {
          render: t('success'),
          pauseOnHover: false,
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        });
        setTimeout(() => {
          recaptchaRef.current?.reset();
        }, 3000);
        resetForm();
      }
    } catch (error) {
      toast.update(toastId, {
        render: t('something_went_wrong'),
        pauseOnHover: false,
        type: 'error',
        isLoading: false,
        autoClose: false,
      });

      resetForm();
      recaptchaRef.current?.reset();
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, setFieldValue }) => (
        <Form className="form-container">
          <h1 className="form-contact-title">
            <Trans i18nKey="tell_us" />
          </h1>

          {/* Name Input */}
          <div className="forms-input">
            <div className="mb-4 position-relative">
              <div className="input-container">
                <FaUser className="input-icon" />
                <Field
                  name="name"
                  type="text"
                  placeholder={t('your_name')}
                  className="form-control"
                />
              </div>
              <ErrorMessage name="name">
                {(msg) => (
                  <div className="span-danger">
                    <p className="p-warning">{msg}</p>
                  </div>
                )}
              </ErrorMessage>
            </div>

            {/* Email Input */}
            <div className="mb-4 position-relative">
              <div className="input-container">
                <MdEmail className="input-icon" />
                <Field
                  name="email"
                  type="email"
                  placeholder={t('your_email')}
                  className="form-control"
                />
              </div>
              <ErrorMessage name="email">
                {(msg) => (
                  <div className="span-danger">
                    <p className="p-warning">{msg}</p>
                  </div>
                )}
              </ErrorMessage>
            </div>

            {/* Subject Input */}
            <div className="mb-4 position-relative">
              <div className="input-container">
                <FaFeatherPointed className="input-icon" />
                <Field
                  name="subject"
                  type="text"
                  placeholder={t('your_subject')}
                  className="form-control"
                />
              </div>
              <ErrorMessage name="subject">
                {(msg) => (
                  <div className="span-danger">
                    <p className="p-warning">{msg}</p>
                  </div>
                )}
              </ErrorMessage>
            </div>

            {/* Message Input */}
            <div className="mb-4 position-relative">
              <div className="input-container">
                <MdMessage className="input-icon" />
                <Field
                  name="message"
                  as="textarea"
                  rows={3}
                  placeholder={t('your_message')}
                  className="form-control"
                />
              </div>
              <ErrorMessage name="message">
                {(msg) => (
                  <div className="span-danger">
                    <p className="p-warning">{msg}</p>
                  </div>
                )}
              </ErrorMessage>
            </div>

            {/* Google reCAPTCHA v2 */}
            <div className="mb-4 position-relative">
              <ReCAPTCHA
                className="captcha"
                ref={recaptchaRef}
                sitekey={captchaKey}
                onChange={(token) => {
                  handleRecaptcha(token);
                  setFieldValue('reCaptchaToken', token);
                }}
              />
              <ErrorMessage name="reCaptchaToken">
                {(msg) => (
                  <div className="span-danger">
                    <p className="p-warning">{msg}</p>
                  </div>
                )}
              </ErrorMessage>
            </div>
          </div>
          {/* Submit Button */}
          <div className="button-div">
            <Button
              type="submit"
              className="button-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? <>{t('submit_request')}</> : t('submit_request')}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
}

export { ContactUs };
